import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CreditCard, ExternalLink } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api.js';
import { Card, StatusBadge, LoadingPanel } from './_shared.jsx';

export default function BillingTab() {
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet('/settings/billing').then((r) => {
      if (r?.ok) setBilling(r.billing);
    }).catch((e) => toast.error(e.message || 'Failed to load billing'));
  }, []);

  async function openPortal() {
    setBusy(true);
    try {
      const r = await apiPost('/stripe/portal', { returnUrl: window.location.href });
      if (r?.url) {
        window.location.href = r.url;
      } else {
        toast.error('Could not open Stripe portal');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Stripe portal failed');
    } finally {
      setBusy(false);
    }
  }

  if (!billing) return <LoadingPanel label="Loading billing…" />;

  return (
    <div className="space-y-4">
      <Card
        title="Billing"
        description="Subscription, payment method, and recent invoices. Detailed plan changes happen in the Stripe Customer Portal."
        actions={
          <button onClick={openPortal} disabled={busy} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50">
            <ExternalLink className="w-4 h-4" /> {busy ? 'Opening…' : 'Stripe Portal'}
          </button>
        }
      >
        <div className="grid md:grid-cols-3 gap-3">
          <div className="p-4 rounded-lg bg-civix-panelLight border border-civix-border/60">
            <div className="text-xs uppercase tracking-wider text-white/40">Current Plan</div>
            <div className="font-display text-2xl font-semibold mt-1 capitalize">{billing.plan}</div>
            <div className="mt-2"><StatusBadge status={billing.status} /></div>
          </div>
          <div className="p-4 rounded-lg bg-civix-panelLight border border-civix-border/60">
            <div className="text-xs uppercase tracking-wider text-white/40 flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5" /> Payment Method
            </div>
            {billing.hasPaymentMethod ? (
              <div className="text-sm text-white/70 mt-2">A payment method is on file.</div>
            ) : (
              <div className="text-sm text-white/45 mt-2">No card on file.</div>
            )}
            <Link to="/billing" className="text-xs text-civix-purpleSoft hover:underline mt-2 inline-block">Update card →</Link>
          </div>
          <div className="p-4 rounded-lg bg-civix-panelLight border border-civix-border/60">
            <div className="text-xs uppercase tracking-wider text-white/40">Next Invoice</div>
            {billing.currentPeriodEnd ? (
              <>
                <div className="font-display text-lg font-semibold mt-1">
                  {new Date(billing.currentPeriodEnd).toLocaleDateString()}
                </div>
                <div className="text-xs text-white/45 mt-1">
                  Last invoice: ${(billing.lastInvoiceTotalUsd || 0).toFixed(2)}
                  {billing.lastInvoiceAt ? ` on ${new Date(billing.lastInvoiceAt).toLocaleDateString()}` : ''}
                </div>
                {billing.cancelAtPeriodEnd && (
                  <div className="text-xs text-amber-300 mt-2">Cancels at period end</div>
                )}
              </>
            ) : (
              <div className="text-sm text-white/45 mt-2">—</div>
            )}
          </div>
        </div>

        {billing.promoCode && (
          <div className="text-xs text-white/50 pt-3 border-t border-civix-border/40">
            Promo applied: <code className="font-mono text-civix-purpleSoft">{billing.promoCode}</code>
          </div>
        )}
      </Card>
    </div>
  );
}
